import PlaylistDataService from '@/services/PlaylistDataService';
import UserPlaylistDataService from '@/services/UserPlaylistDataService';
import PlaylistTuneDataService from '@/services/PlaylistTuneDataService';
import RatingDataService from '@/services/RatingDataService';
function state() {
    return {
        currentPlaylist: null,
        currentPlaylistTunes: [],
        activePlaylists: [],
        inactivePlaylists: [],
        loadingCurrentPlaylist: false,
        loadingCurrentPlaylistTunes: false,
        loadingPlaylists: false,
        lastError: null,
    };
}
function toPlaylistIdString(id) {
    return String(id);
}
function getErrorMessage(error) {
    if (error instanceof Error && error.message) {
        return error.message;
    }
    return 'Playlist request failed';
}
function toTargetParams(target) {
    if (!target?.targetType || target.targetId === undefined || target.targetId === null) {
        return {};
    }
    return { target_type: target.targetType, target_id: target.targetId };
}
const getters = {
    currentPlaylist: (currentState) => currentState.currentPlaylist,
    currentPlaylistTunes: (currentState) => currentState.currentPlaylistTunes,
    activePlaylists: (currentState) => currentState.activePlaylists,
    inactivePlaylists: (currentState) => currentState.inactivePlaylists,
    pageTitle: (currentState) => currentState.currentPlaylist?.title ?? '',
    loadingCurrentPlaylist: (currentState) => currentState.loadingCurrentPlaylist,
    loadingCurrentPlaylistTunes: (currentState) => currentState.loadingCurrentPlaylistTunes,
    loadingPlaylists: (currentState) => currentState.loadingPlaylists,
    lastError: (currentState) => currentState.lastError,
};
const mutations = {
    SET_LOADING_CURRENT_PLAYLIST(currentState, loading) {
        currentState.loadingCurrentPlaylist = loading;
    },
    SET_LOADING_CURRENT_PLAYLIST_TUNES(currentState, loading) {
        currentState.loadingCurrentPlaylistTunes = loading;
    },
    SET_CURRENT_PLAYLIST(currentState, currentPlaylist) {
        currentState.currentPlaylist = currentPlaylist;
    },
    SET_CURRENT_PLAYLIST_TUNES(currentState, tunes) {
        currentState.currentPlaylistTunes = tunes;
    },
    SET_ACTIVE_PLAYLISTS(currentState, playlists) {
        currentState.activePlaylists = playlists;
    },
    SET_INACTIVE_PLAYLISTS(currentState, playlists) {
        currentState.inactivePlaylists = playlists;
    },
    SET_LOADING_PLAYLISTS(currentState, loading) {
        currentState.loadingPlaylists = loading;
    },
    CLEAR_CURRENT_PLAYLIST(currentState) {
        currentState.currentPlaylist = null;
        currentState.currentPlaylistTunes = [];
    },
    /* TODO:
     * Skapa en error store
     * time out för att inte få en permanent error
     * error toast i error store
     */
    SET_LAST_ERROR(currentState, message) {
        currentState.lastError = message;
    },
};
const actions = {
    async loadPlaylist({ commit, dispatch }, id) {
        if (id === undefined || id === '') {
            return;
        }
        commit('SET_LAST_ERROR', null);
        commit('SET_LOADING_CURRENT_PLAYLIST', true);
        commit('CLEAR_CURRENT_PLAYLIST');
        try {
            const response = await PlaylistDataService.get(id);
            commit('SET_CURRENT_PLAYLIST', response.data);
            await dispatch('fetchCurrentPlaylistTunes', toPlaylistIdString(id));
        }
        catch (error) {
            commit('SET_LAST_ERROR', getErrorMessage(error));
            // Leave currentPlaylist null on fetch failure.
        }
        finally {
            commit('SET_LOADING_CURRENT_PLAYLIST', false);
        }
    },
    async joinPlaylist({ commit, dispatch }, id) {
        commit('SET_LAST_ERROR', null);
        try {
            await UserPlaylistDataService.update(id, { active: true });
            await dispatch('loadPlaylist', id);
        }
        catch (error) {
            commit('SET_LAST_ERROR', getErrorMessage(error));
        }
    },
    async reactivatePlaylist({ commit, dispatch }, id) {
        commit('SET_LAST_ERROR', null);
        try {
            await PlaylistDataService.update(id, { active: true });
            await dispatch('loadPlaylist', id);
        }
        catch (error) {
            commit('SET_LAST_ERROR', getErrorMessage(error));
        }
    },
    async updatePlaylist({ commit, dispatch, state: currentState }, payload) {
        commit('SET_LAST_ERROR', null);
        const { id, data } = payload;
        try {
            await PlaylistDataService.update(id, data);
            if (currentState.currentPlaylist &&
                toPlaylistIdString(currentState.currentPlaylist.id ?? '') === toPlaylistIdString(id)) {
                await dispatch('loadPlaylist', id);
            }
        }
        catch (error) {
            commit('SET_LAST_ERROR', getErrorMessage(error));
        }
    },
    async setPlaylistActive({ dispatch }, payload) {
        await dispatch('updatePlaylist', { id: payload.id, data: { active: payload.active } });
    },
    async setPlaylistLocked({ dispatch }, payload) {
        await dispatch('updatePlaylist', { id: payload.id, data: { locked: payload.locked } });
    },
    async setPlaylistMetadata({ dispatch }, payload) {
        await dispatch('updatePlaylist', { id: payload.id, data: payload.data });
    },
    async setUserPlaylistActive({ commit, dispatch }, payload) {
        commit('SET_LAST_ERROR', null);
        try {
            await UserPlaylistDataService.update(payload.id, { active: payload.active });
            if (payload.active) {
                await dispatch('loadPlaylist', payload.id);
            }
        }
        catch (error) {
            commit('SET_LAST_ERROR', getErrorMessage(error));
        }
    },
    async createPlaylist({ commit }, data) {
        commit('SET_LAST_ERROR', null);
        try {
            const response = await PlaylistDataService.create(data);
            return response.data;
        }
        catch (error) {
            commit('SET_LAST_ERROR', getErrorMessage(error));
            return null;
        }
    },
    async fetchActivePlaylists({ commit }, target) {
        commit('SET_LAST_ERROR', null);
        commit('SET_LOADING_PLAYLISTS', true);
        try {
            const response = await PlaylistDataService.getAll({ active: true, ...toTargetParams(target) });
            commit('SET_ACTIVE_PLAYLISTS', response.data ?? []);
        }
        catch (error) {
            commit('SET_LAST_ERROR', getErrorMessage(error));
            commit('SET_ACTIVE_PLAYLISTS', []);
        }
        finally {
            commit('SET_LOADING_PLAYLISTS', false);
        }
    },
    async fetchInactivePlaylists({ commit }, target) {
        commit('SET_LAST_ERROR', null);
        commit('SET_LOADING_PLAYLISTS', true);
        try {
            const response = await PlaylistDataService.getAll({ active: false, ...toTargetParams(target) });
            commit('SET_INACTIVE_PLAYLISTS', response.data ?? []);
        }
        catch (error) {
            commit('SET_LAST_ERROR', getErrorMessage(error));
            commit('SET_INACTIVE_PLAYLISTS', []);
        }
        finally {
            commit('SET_LOADING_PLAYLISTS', false);
        }
    },
    async addTuneToCurrentPlaylist({ commit, dispatch }, payload) {
        commit('SET_LAST_ERROR', null);
        try {
            await PlaylistTuneDataService.update(payload.playlistId, {
                tune_id: payload.tuneId,
                average_score: payload.averageScore,
                sum_level: payload.sumLevel,
            });
            await dispatch('fetchCurrentPlaylistTunes', payload.playlistId);
        }
        catch (error) {
            commit('SET_LAST_ERROR', getErrorMessage(error));
        }
    },
    async removeTuneFromCurrentPlaylist({ commit, dispatch }, payload) {
        commit('SET_LAST_ERROR', null);
        try {
            await PlaylistTuneDataService.delete(payload.playlistId, { tune_id: payload.tuneId });
            await dispatch('fetchCurrentPlaylistTunes', payload.playlistId);
        }
        catch (error) {
            commit('SET_LAST_ERROR', getErrorMessage(error));
        }
    },
    async reorderCurrentPlaylistTunes({ commit }, payload) {
        commit('SET_LAST_ERROR', null);
        try {
            await PlaylistTuneDataService.update(payload.playlistId, {
                from: payload.from,
                to: payload.to,
            });
            return true;
        }
        catch (error) {
            commit('SET_LAST_ERROR', getErrorMessage(error));
            return false;
        }
    },
    async updateTuneRating({ commit }, payload) {
        commit('SET_LAST_ERROR', null);
        try {
            await RatingDataService.update(payload.tuneId, {
                rating: payload.rating,
                level: payload.level,
            });
        }
        catch (error) {
            commit('SET_LAST_ERROR', getErrorMessage(error));
        }
    },
    async fetchCurrentPlaylistTunes({ commit }, id) {
        if (id === undefined || id === '') {
            return;
        }
        commit('SET_LAST_ERROR', null);
        commit('SET_LOADING_CURRENT_PLAYLIST_TUNES', true);
        commit('SET_CURRENT_PLAYLIST_TUNES', []);
        try {
            const response = await PlaylistTuneDataService.getAll({
                playlist_id: toPlaylistIdString(id),
                includedTunes: true,
            });
            commit('SET_CURRENT_PLAYLIST_TUNES', response.data ?? []);
        }
        catch (error) {
            commit('SET_LAST_ERROR', getErrorMessage(error));
            commit('SET_CURRENT_PLAYLIST_TUNES', []);
        }
        finally {
            commit('SET_LOADING_CURRENT_PLAYLIST_TUNES', false);
        }
    },
};
export const playlist = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
